import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import KmlCharts from '../components/KmlCharts'; 
import FixedIntervalChart from '../components/FixedIntervalChart';
import SpeedChart from '../components/SpeedChart';
import { useLocation } from 'react-router-dom';
import ALRMapModal from '../components/ALRMapModal';
import ALRSummary from '../components/ALRSummary'; 
import GrindPlanner from '../components/GrindPlanner';
import RollingStraightedge from '../components/RollingStraightedge';
import PayAdjustmentPanel from '../components/pay/PayAdjustmentPanel';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const AnalysisPage = ({ 
  sessionData = null,
  segmentsData = null,
  eventsData = null,
  projectData = null,
  projectConfigData = null,
  onAlrRowClick,
  iriProfileData = null,
  elevationProfileData = null,
  kmlLoading = false,
  kmlError = null,
  targetSegmentId = null,
  onClearTargetSegment
}) => {
  const [session, setSession] = useState(sessionData);
  const [segments, setSegments] = useState(segmentsData || []);
  const [events, setEvents] = useState(eventsData || []);
  const [project, setProject] = useState(projectData);
  const [projectConfig, setProjectConfig] = useState(projectConfigData);
  const [selectedSegmentNumber, setSelectedSegmentNumber] = useState(null);
  const [activeChart, setActiveChart] = useState('intervals');
  const [visibleLayers, setVisibleLayers] = useState({
    leftIRI: false,
    rightIRI: false,
    avgIRI: true
  });
  const [selectedALRForMap, setSelectedALRForMap] = useState(null);
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);

  const location = useLocation();

  const DEFAULT_IRI_RANGES = [
    {"label": "Excellent", "max": 95, "description": "< 95 in/mi"},
    {"label": "Good", "max": 120, "description": "95-120 in/mi"},
    {"label": "Fair", "max": 170, "description": "120-170 in/mi"},
    {"label": "Poor", "max": 270, "description": "170-270 in/mi"},
    {"label": "Very Poor", "max": 9999, "description": "> 270 in/mi"}
  ];

  const dataSegments = segmentsData || segments;
  const dataEvents = eventsData || events;

  const getWorkDirection = () => {
    if (sessionData?.meta?.is_reverse_stationing === true) {
      return 'reversed';
    }
    return 'normal';
  };

  const workDirection = getWorkDirection();
  const isReversedDirection = workDirection === 'reversed';

  const getIRIThresholds = () => {
  if (!projectConfig) {
    return { typeA: 160, typeB: 170, active: 170 };
  }
  const typeA = projectConfig.iri_threshold_type_a || 160;
  const typeB = projectConfig.iri_threshold_type_b || 170;
  const active = typeB;
  return { typeA, typeB, active };
};

  const selectedAlrFilter = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const alrIdsParam = searchParams.get('selected_alrs');
    if (!alrIdsParam) return null;
    return alrIdsParam.split(',').map(id => parseInt(id, 10)).filter(id => !isNaN(id));
  }, [location.search]);

  useEffect(() => {
    if (targetSegmentId) {
      const segmentIndex = dataSegments.findIndex(s => s.segment_id === targetSegmentId);
      if (segmentIndex !== -1) {
        const segNum = segmentIndex + 1;
        setSelectedSegmentNumber(segNum);
        setActiveChart('alr');
      }
    }
  }, [targetSegmentId, dataSegments]);

  useEffect(() => {
    setSession(sessionData);
    setSegments(segmentsData || []);
    setEvents(eventsData || []);
    setProject(projectData);
    setProjectConfig(projectConfigData);
  }, [sessionData, segmentsData, eventsData, projectData, projectConfigData]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    if (tabParam === 'analysis' && selectedAlrFilter) {
      setActiveChart('alr');
    }
  }, [location.search, selectedAlrFilter]);

  const formatStation = useCallback((distance) => {
    const hundreds = Math.floor(distance / 100);
    const remainder = distance % 100;
    return `${hundreds}+${remainder.toFixed(0).padStart(2, '0')}`;
  }, []);

  const handleAlrRowClick = useCallback((defect) => {
    const alrEventForModal = {
      event_id: defect.eventId,
      from_station_ft: defect.startDistance,
      to_station_ft: defect.stopDistance,
      peak_station_ft: defect.peakDistance,
      magnitude_in: defect.magnitude,
      wheelpath: defect.wheelpath,
      geom: dataEvents?.find(e => e.event_id === defect.eventId)?.geom,
      event_type: 'LR'
    };
    setSelectedALRForMap(alrEventForModal);
    setIsMapModalOpen(true);
  }, [dataEvents]);

  const COLOR_PALETTES = {
    2: ['#2EFF2E', '#D50000'],
    3: ['#2EFF2E', '#FFEB3B', '#D50000'],
    4: ['#2EFF2E', '#00A300', '#FFEB3B', '#D50000'],
    5: ['#2EFF2E', '#00A300', '#FFEB3B', '#FFA500', '#D50000'],
  };

  const IRI_COLOR_SCALE = useMemo(() => {
    const customRanges = projectConfig?.iri_color_scale;
    const isValidScale = Array.isArray(customRanges) && 
      customRanges.length >= 2 && 
      customRanges.length <= 5;
    const sourceRanges = isValidScale ? customRanges : DEFAULT_IRI_RANGES;
    const colors = COLOR_PALETTES[sourceRanges.length] || COLOR_PALETTES[5];
    const finalRanges = sourceRanges.map((range, index) => ({
      ...range,
      color: colors[index]
    }));
    return {
      ranges: finalRanges,
      exceptionColor: '#00FFFF',
      defaultColor: '#A0A0A0'
    };
  }, [projectConfig]);

  const getIRIColor = useCallback((iri) => {
    if (iri <= 0) return '#A0A0A0'; 
    for (const range of IRI_COLOR_SCALE.ranges) {
      if (iri <= range.max) return range.color;
    }
    return IRI_COLOR_SCALE.ranges[IRI_COLOR_SCALE.ranges.length - 1].color;
  }, [IRI_COLOR_SCALE]);

  const thresholds = getIRIThresholds();
  const analysisStart = session?.analysis_start_station ?? session?.start_station_ft ?? 0;
  const analysisEnd = session?.analysis_end_station ?? session?.end_station_ft ?? 0;

  const iriData = useMemo(() => {
    return dataSegments.map(seg => ({
      distance: seg.from_station_ft ?? 0,
      station: formatStation(seg.from_station_ft ?? 0),
      leftIRI: seg.exception ? null : (seg.iri_left_in_per_mi ?? 0),
      rightIRI: seg.exception ? null : (seg.iri_right_in_per_mi ?? 0),
      avgIRI: seg.exception ? null : (seg.iri_avg_in_per_mi ?? 0),
      isException: seg.exception
    })).sort((a, b) => a.distance - b.distance);
  }, [dataSegments, formatStation]);

  const alrDefects = useMemo(() => {
    return dataEvents?.filter(event =>  
      event.event_type === 'LR' && 
      (event.magnitude_in && event.magnitude_in > thresholds.active)
    ).map(event => ({
      eventId: event.event_id,
      startDistance: event.from_station_ft || 0,
      peakDistance: event.peak_station_ft || 0,
      stopDistance: event.to_station_ft || 0,
      magnitude: event.magnitude_in || 0,
      wheelpath: event.wheelpath,
      length: (event.to_station_ft || 0) - (event.from_station_ft || 0),
      units: event.units || 'in'
    })).sort((a, b) => a.startDistance - b.startDistance) || [];
  }, [dataEvents, thresholds.active]);

  const processedEventsData = useMemo(() => {
    return dataEvents?.map(event => ({
      distance: parseFloat(event.from_station_ft) || 0,
      station: formatStation(parseFloat(event.from_station_ft) || 0),
      peakStation: parseFloat(event.peak_station_ft) || 0,
      endStation: parseFloat(event.to_station_ft) || 0,
      magnitude: parseFloat(event.magnitude_in) || 0,
      eventType: event.event_type,
      wheelpath: event.wheelpath,
      eventId: event.event_id
    })).sort((a, b) => a.distance - b.distance) || [];
  }, [dataEvents, formatStation]);

  const surfaceEvents = useMemo(() => 
    processedEventsData.filter(e => e.eventType === 'Bump' || e.eventType === 'Dip'), 
  [processedEventsData]);

  const renderIRIChart = () => {
    const datasets = [];
    const allIRIValues = iriData
      .flatMap(d => [d.leftIRI, d.rightIRI, d.avgIRI])
      .filter(v => v !== null && v !== undefined && v > 0);
    
    const minIRI = allIRIValues.length > 0 ? Math.min(...allIRIValues) : 0;
    const maxIRI = allIRIValues.length > 0 ? Math.max(...allIRIValues) : 100;
    const padding = (maxIRI - minIRI) * 0.1;
    const yMin = Math.max(0, Math.floor((minIRI - padding) / 10) * 10);
    const yMax = Math.ceil((maxIRI + padding) / 10) * 10;
    
    if (visibleLayers.avgIRI) {
      datasets.push({
        label: 'MRI',
        data: iriData.map(d => ({ x: d.distance, y: d.avgIRI })),
        borderColor: '#9333ea',
        backgroundColor: 'rgba(147, 51, 234, 0.1)',
        borderWidth: 2,
        fill: false,
        tension: 0.4,
        pointRadius: 1,
        pointHoverRadius: 3,
        spanGaps: false
      });
    }
    if (visibleLayers.leftIRI) {
      datasets.push({
        label: 'Left IRI',
        data: iriData.map(d => ({ x: d.distance, y: d.leftIRI })),
        borderColor: '#2563eb',
        backgroundColor: 'rgba(37, 99, 235, 0.1)',
        borderWidth: 1.5,
        fill: false,
        tension: 0.4,
        pointRadius: 1,
        pointHoverRadius: 3,
        spanGaps: false
      });
    }
    if (visibleLayers.rightIRI) {
      datasets.push({
        label: 'Right IRI',
        data: iriData.map(d => ({ x: d.distance, y: d.rightIRI })),
        borderColor: '#16a34a',
        backgroundColor: 'rgba(22, 163, 74, 0.1)',
        borderWidth: 1.5,
        fill: false,
        tension: 0.4,
        pointRadius: 1,
        pointHoverRadius: 3,
        spanGaps: false
      });
    }

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (context) => `Sta ${formatStation(context[0].parsed.x)}`,
            label: (context) => `${context.dataset.label}: ${context.parsed.y.toFixed(1)} in/mi`
          }
        }
      },
      scales: {
        x: {
          type: 'linear',
          min: isReversedDirection ? analysisEnd : analysisStart,
          max: isReversedDirection ? analysisStart : analysisEnd,
          reverse: isReversedDirection,
          title: { display: true, text: 'Station', font: { size: 10 } },
          ticks: { callback: (value) => formatStation(value), font: { size: 9 } }
        },
        y: {
          type: 'linear',
          position: 'left',
          min: yMin,
          max: yMax,
          title: { display: true, text: 'IRI (in/mi)', font: { size: 10 } },
          ticks: { font: { size: 9 } }
        },
      }
    };

    return (
      <div className="bg-white border border-gray-300">
        <div className="bg-gray-100 px-3 py-2 border-b border-gray-300 flex items-center justify-between">
          <span className="text-xs font-medium text-gray-700">
            IRI Analysis {isReversedDirection && <span className="text-blue-600">(Rev)</span>}
          </span>
          <div className="flex items-center gap-2">
            <button 
              onClick={() => setVisibleLayers(prev => ({ ...prev, leftIRI: !prev.leftIRI }))} 
              className={`flex items-center px-2 py-0.5 text-xs border ${visibleLayers.leftIRI ? 'bg-blue-50 border-blue-400 text-blue-700' : 'bg-white border-gray-300 text-gray-500'}`}
            >
              {visibleLayers.leftIRI ? <EyeIcon className="w-3 h-3 mr-1" /> : <EyeOffIcon className="w-3 h-3 mr-1" />}
              L
            </button>
            <button 
              onClick={() => setVisibleLayers(prev => ({ ...prev, rightIRI: !prev.rightIRI }))} 
              className={`flex items-center px-2 py-0.5 text-xs border ${visibleLayers.rightIRI ? 'bg-green-50 border-green-400 text-green-700' : 'bg-white border-gray-300 text-gray-500'}`}
            >
              {visibleLayers.rightIRI ? <EyeIcon className="w-3 h-3 mr-1" /> : <EyeOffIcon className="w-3 h-3 mr-1" />}
              R
            </button>
            <button 
              onClick={() => setVisibleLayers(prev => ({ ...prev, avgIRI: !prev.avgIRI }))} 
              className={`flex items-center px-2 py-0.5 text-xs border ${visibleLayers.avgIRI ? 'bg-purple-50 border-purple-400 text-purple-700' : 'bg-white border-gray-300 text-gray-500'}`}
            >
              {visibleLayers.avgIRI ? <EyeIcon className="w-3 h-3 mr-1" /> : <EyeOffIcon className="w-3 h-3 mr-1" />}
              MRI
            </button>
          </div>
        </div>
        <div className="p-3">
          <div style={{ height: '350px' }}>
            <Line data={{ datasets }} options={options} />
          </div>
        </div>
      </div>
    );
  };

  const chartTabs = [
    { id: 'intervals', name: 'Fixed Interval' },
    { id: 'alr', name: 'Localized Roughness' },
    { id: 'events', name: 'Rolling Straightedge' },
    { id: 'speed', name: 'Speed Profile' },
    { id: 'kml_profiles', name: 'IRI / Elevation' },
    // { id: 'grind_plan', name: 'Grind Planner' }, // Hidden - not ready for release
    { id: 'pay_calc', name: 'Pay Calculator' },
  ];

  return (
    <div>
      {/* Compact tab bar - integrated with content below */}
      <div className="bg-white border-b border-gray-200 flex">
        {chartTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveChart(tab.id)}
            className={`
              px-4 py-2 text-sm font-medium
              transition-colors border-b-2
              ${activeChart === tab.id
                ? 'border-red-600 text-gray-900 bg-white'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }
            `}
          >
            {tab.name}
            {tab.id === 'kml_profiles' && kmlLoading && (
              <span className="ml-1 inline-block w-2 h-2 border border-gray-400 border-t-transparent rounded-full animate-spin" />
            )}
          </button>
        ))}
      </div>

      {/* Content - no top border since tabs provide it */}
      <div>
        {activeChart === 'iri' && renderIRIChart()}
        {activeChart === 'speed' && (
          <SpeedChart
            segments={dataSegments}
            isReversed={isReversedDirection}
            formatStation={formatStation}
            analysisStart={analysisStart}
            analysisEnd={analysisEnd}
          />
        )}
        
        {activeChart === 'events' && (
          <RollingStraightedge
            events={dataEvents}
            isReversed={isReversedDirection}
            formatStation={formatStation}
            session={session}
          />
        )}
        
        {activeChart === 'intervals' && (
          <FixedIntervalChart 
            segments={dataSegments}
            isReversed={isReversedDirection}
            thresholds={thresholds}
            formatStation={formatStation}
            getIRIColor={getIRIColor}
            colorScale={IRI_COLOR_SCALE}
            events={dataEvents}
            session={session}
            onSegmentSelect={(segmentNumber) => {
              setSelectedSegmentNumber(segmentNumber);
              setActiveChart('alr');
            }}
          />
        )}

        {activeChart === 'alr' && (
          <ALRSummary 
            alrDefects={alrDefects}
            dataEvents={dataEvents}
            thresholds={thresholds}
            formatStation={formatStation}
            sessionId={session?.id}
            onAlrRowClick={handleAlrRowClick}
            dataSegments={dataSegments}
            projectConfig={projectConfig}
            selectedSegmentNumber={selectedSegmentNumber}
            onSegmentSelect={(segNum) => {
              setSelectedSegmentNumber(segNum);
              if (segNum === null && onClearTargetSegment) {
                onClearTargetSegment();
              }
            }}            
          />
        )}

        {activeChart === 'kml_profiles' && (
          <KmlCharts
            activeChart={activeChart}
            iriProfileData={iriProfileData}
            elevationProfileData={elevationProfileData}
            thresholds={thresholds}
            kmlLoading={kmlLoading}
            kmlError={kmlError}
            sessionData={session}
            alrDefects={alrDefects}
            allSegments={dataSegments}
            surfaceEvents={surfaceEvents}
            isReversed={isReversedDirection}
          />
        )}

        {activeChart === 'grind_plan' && (
          <GrindPlanner 
            events={dataEvents}        
            segments={dataSegments}    
            projectConfig={projectConfig} 
            iriProfileData={iriProfileData}
            elevationProfileData={elevationProfileData}
          />
        )}

        {activeChart === 'pay_calc' && (
          <PayAdjustmentPanel
            project={project}
            session={session}
            segments={dataSegments}
            alrDefects={alrDefects}
            isReversed={isReversedDirection}
            surfaceEvents={surfaceEvents}
            onProjectUpdate={setProject}
          />
        )}

        
      </div>

      {isMapModalOpen && selectedALRForMap && (
        <ALRMapModal
          alrEvent={selectedALRForMap}
          allALREvents={
            (() => {
              const searchParams = new URLSearchParams(location.search);
              const alrIdsParam = searchParams.get('selected_alrs');
              
              let defectsToShow = alrDefects;
              if (alrIdsParam) {
                const filterIds = alrIdsParam.split(',').map(id => parseInt(id, 10)).filter(id => !isNaN(id));
                defectsToShow = alrDefects.filter(d => filterIds.includes(d.eventId));
              }
              
              return defectsToShow.map(defect => ({
                event_id: defect.eventId,
                from_station_ft: defect.startDistance,
                to_station_ft: defect.stopDistance,
                peak_station_ft: defect.peakDistance,
                magnitude_in: defect.magnitude,
                wheelpath: defect.wheelpath,
                geom: dataEvents?.find(e => e.event_id === defect.eventId)?.geom,
                event_type: 'LR'
              }));
            })()
          }
          events={dataEvents}
          onClose={() => {
            setIsMapModalOpen(false);
            setSelectedALRForMap(null);
          }}
        />
      )}
    </div>
  );
};

export default AnalysisPage;